import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause } from "lucide-react";
import { useLang } from "@/lib/i18n";

const EASE = [0.16, 1, 0.3, 1];

const LAYERS = [
  { key: "top", labelKey: "ritual.top", notesKey: "notes_top", descKey: "notes_top_desc", fallbackKey: "ritual.fTop", width: "md:w-2/3" },
  { key: "heart", labelKey: "ritual.heart", notesKey: "notes_heart", descKey: "notes_heart_desc", fallbackKey: "ritual.fHeart", width: "md:w-4/5" },
  { key: "base", labelKey: "ritual.base", notesKey: "notes_base", descKey: "notes_base_desc", fallbackKey: "ritual.fBase", width: "md:w-full" },
];

export const ScentRitual = ({ product }) => {
  const { t, pick } = useLang();
  const [active, setActive] = useState("base");
  const [auto, setAuto] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    setActive("base");
    setAuto(false);
  }, [product?.slug]);

  useEffect(() => {
    if (timer.current) clearInterval(timer.current);
    if (auto) {
      timer.current = setInterval(() => {
        setActive((cur) => {
          const order = ["base", "heart", "top"];
          return order[(order.indexOf(cur) + 1) % order.length];
        });
      }, 2800);
    }
    return () => clearInterval(timer.current);
  }, [auto]);

  useEffect(() => () => clearInterval(timer.current), []);

  const layer = LAYERS.find((l) => l.key === active);

  return (
    <div data-testid="scent-ritual" className="border border-gold/15 bg-obsidian/70 p-5 sm:p-6">
      <div className="flex items-center justify-between mb-5">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/85">{t("ritual.title")}</p>
        <button
          data-testid="scent-ritual-toggle"
          onClick={() => setAuto((a) => !a)}
          className="flex items-center gap-2 border border-gold/30 rounded-full px-4 py-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-gold hover:bg-gold/10 transition-colors duration-300"
        >
          {auto ? <Pause size={11} /> : <Play size={11} />}
          {auto ? t("ritual.stop") : t("ritual.start")}
        </button>
      </div>

      <div className="flex flex-col gap-2">
        {LAYERS.map((l) => {
          const selected = active === l.key;
          return (
            <button
              key={l.key}
              data-testid={`scent-layer-${l.key}`}
              onClick={() => {
                setAuto(false);
                setActive(l.key);
              }}
              className={`${l.width} mx-auto w-full border px-4 py-2.5 flex items-center justify-between transition-all duration-500 ${
                selected ? "border-gold bg-gold/15 text-gold" : "border-cream/10 text-mist hover:border-gold/40"
              }`}
            >
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase">{t(l.labelKey)}</span>
              {selected && (
                <span className="h-1.5 w-1.5 rounded-full bg-gold shadow-[0_0_8px_rgba(212,175,55,0.9)]" />
              )}
            </button>
          );
        })}
      </div>

      <motion.div
        key={active}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: EASE }}
        className="mt-5 text-center"
      >
        <p className="font-serif italic text-gold/90 text-lg">{pick(product, layer.notesKey)}</p>
        <p className="mt-2 text-sm text-mist leading-relaxed">
          {pick(product, layer.descKey) || t(layer.fallbackKey)}
        </p>
      </motion.div>
    </div>
  );
};

export default ScentRitual;
